// Drives the MFA setup modal (templates/authentication/mfa/_modal.html.slim). It uses Hanko's profile flow directly, rather than a Hanko web component, so
// that the modal only offers MFA setup and matches the rest of the portal. The `data-hanko-mfa-setup` dialog carries
// the runtime config:
//
//   data-hanko-api-url   Hanko project base URL (from settings.hanko_public_url)
//   data-hanko-user-id   Hanko id of the logged-in user
//   data-login-url       Where to send the user if they need to log in again
//
// The modal steps through an introduction, installing an authenticator app, scanning the QR code and entering a code
// from the app. Hanko is only asked for a QR code once the clinician chooses to set up 2FA. Once Hanko has saved the
// authenticator app we submit the modal's confirm form. The server checks with Hanko that setup really happened before
// treating the session as MFA-verified.
//
// The modal opens as soon as it's on the page. On most pages it's fetched with htmx by the MFA banner's "Set up now", and
// can be closed again. On the MFA setup page at the end of onboarding it is `data-mfa-standalone`, and is skipped with
// its own links, as closing it would leave an empty page.
//
// Setup is reported to GTM with the same events as the login widget (see hanko_flow_tracking.js):
//
//   hanko_view_setup_authenticator_app     the QR code was fetched
//   hanko_submit_setup_authenticator_app   a code was submitted
//   hanko_view_mfa_setup_success           Hanko accepted the code and saved the authenticator app
//   hanko_flow_error                       Hanko rejected the code, or setup failed

import { Hanko } from "@teamhanko/hanko-elements";
import { pushFlowEvent, registerFlowTracking } from "./hanko_flow_tracking.js";

// Keep this flow's cached state apart from the login widget's (which uses Hanko's default key).
const FLOW_CACHE_KEY = "hanko-mfa-setup-flow-state";

// Hanko's service.name, which it uses as the issuer in the QR code, so that tapping the code adds the same entry to the
// authenticator app as scanning it.
const OTP_ISSUER = "oVRcome";

// The otpauth:// link that opens an authenticator app on the same device, built from the same details as the QR code:
// the issuer, and the user's primary email address (or their username, if they don't have one).
function otpAuthUrl(secret, user) {
  const accountName = user.emails?.find((email) => email.is_primary)?.address ?? user.username?.username;
  const label = [OTP_ISSUER, accountName].filter(Boolean).map(encodeURIComponent).join(":");
  const parameters = new URLSearchParams({ secret, issuer: OTP_ISSUER });
  return `otpauth://totp/${label}?${parameters}`;
}

// Wires up the row of single-digit boxes the code is entered into, and returns the boxes. Typing a
// digit moves on to the next box, Backspace moves back, and a pasted or autofilled code is spread
// across the boxes. The form is submitted as soon as every box is filled.
function setUpCodeInputs(codeForm) {
  const codeInputs = Array.from(codeForm.querySelectorAll("[data-mfa-code-digit]"));

  // Put the digits found in `text` into the boxes from `startIndex` onwards
  function enterDigits(startIndex, text) {
    const digits = text.replace(/\D/g, "").slice(0, codeInputs.length - startIndex);
    if (digits.length === 0) return;

    Array.from(digits).forEach((digit, offset) => {
      codeInputs[startIndex + offset].value = digit;
    });

    if (codeInputs.every((codeInput) => codeInput.value)) {
      codeForm.requestSubmit();
    } else {
      codeInputs[Math.min(startIndex + digits.length, codeInputs.length - 1)].focus();
    }
  }

  codeInputs.forEach((codeInput, index) => {
    const previousInput = codeInputs[index - 1];
    const nextInput = codeInputs[index + 1];

    // Select the box's digit so that typing replaces it
    codeInput.addEventListener("focus", () => codeInput.select());

    codeInput.addEventListener("input", (event) => {
      // A typed digit replaces the one in the box. Autofill has no `data`, and puts the whole code
      // in the box's value.
      const enteredText = event.data ?? codeInput.value;
      codeInput.value = codeInput.value.replace(/\D/g, "").slice(0, 1);
      enterDigits(index, enteredText);
    });

    codeInput.addEventListener("paste", (event) => {
      event.preventDefault();
      const pastedDigits = event.clipboardData.getData("text").replace(/\D/g, "");
      // A whole code replaces whatever has been entered, whichever box it's pasted into
      enterDigits(pastedDigits.length >= codeInputs.length ? 0 : index, pastedDigits);
    });

    codeInput.addEventListener("keydown", (event) => {
      if (event.key === "Backspace" && !codeInput.value && previousInput) {
        event.preventDefault();
        previousInput.value = "";
        previousInput.focus();
      } else if (event.key === "ArrowLeft" && previousInput) {
        event.preventDefault();
        previousInput.focus();
      } else if (event.key === "ArrowRight" && nextInput) {
        event.preventDefault();
        nextInput.focus();
      }
    });
  });

  return codeInputs;
}

function mountMfaSetup(dialog) {
  const hanko = new Hanko(dialog.dataset.hankoApiUrl);
  registerFlowTracking(hanko);
  const steps = Array.from(dialog.querySelectorAll("[data-mfa-step]"));
  const loadingElement = dialog.querySelector("[data-mfa-loading]");
  const qrDetailsElement = dialog.querySelector("[data-mfa-qr-details]");
  const qrCodeImage = dialog.querySelector("[data-mfa-qr-code]");
  const otpLink = dialog.querySelector("[data-mfa-otp-link]");
  const secretElement = dialog.querySelector("[data-mfa-secret]");
  const secretCopiedElement = dialog.querySelector("[data-mfa-secret-copied]");
  const setupRequiredButtons = dialog.querySelectorAll("[data-mfa-requires-setup]");
  const codeForm = dialog.querySelector("[data-mfa-code-form]");
  const codeInputs = setUpCodeInputs(codeForm);
  const codeErrorElement = codeForm.querySelector("[data-mfa-code-error]");
  const codeHelpToggle = codeForm.querySelector("[data-mfa-code-help-toggle]");
  const codeHelpElement = document.getElementById(codeHelpToggle.getAttribute("aria-controls"));
  const submitButton = codeForm.querySelector("button[type='submit']");
  const confirmForm = dialog.querySelector("[data-mfa-confirm-form]");

  // Each flow state's actions can only be run once, so this always holds the latest state.
  let currentState;
  // Fetching the QR code starts when the clinician chooses to set up 2FA, and only happens once unless setup fails
  let setupStarted = false;

  function showStep(stepName) {
    steps.forEach((step) => {
      step.hidden = step.dataset.mfaStep !== stepName;
    });
    if (stepName === "verify") codeInputs[0].focus();
  }

  function showError(error) {
    console.error("Authenticator app setup failed", error);
    // The flow can't carry on from a failure, so the next attempt starts again with a new QR code, rather than
    // waiting on one that will never arrive
    setupStarted = false;
    loadingElement.hidden = false;
    qrDetailsElement.hidden = true;
    setupRequiredButtons.forEach((button) => {
      button.disabled = true;
    });
    showStep("error");
  }

  // Move on from a state. Returns false (after showing the error step) if Hanko returned an error.
  function enterState(state) {
    currentState = state;
    if (state.name === "error") {
      showError(state.error);
      return false;
    }
    return true;
  }

  // Ask Hanko for a new authenticator app secret, and show its QR code
  async function fetchQrCode() {
    // The Hanko session expires separately from ours, and the browser could be logged in to Hanko as
    // someone else, so check we're setting up MFA on the right account.
    const session = await hanko.validateSession();
    if (!session.is_valid || session.user_id !== dialog.dataset.hankoUserId) {
      window.location.assign(dialog.dataset.loginUrl);
      return;
    }

    const profileState = await hanko.createState("profile", { cacheKey: FLOW_CACHE_KEY, loadFromCache: false });
    if (!enterState(profileState)) return;

    // No setup action means Hanko already has an authenticator app for this user (e.g. setup finished
    // but the confirm form was never submitted), so let the server confirm it.
    const continueToSetup = profileState.actions.continue_to_otp_secret_creation;
    if (!continueToSetup.enabled) {
      confirmForm.submit();
      return;
    }

    const setupState = await continueToSetup.run();
    if (!enterState(setupState)) return;

    qrCodeImage.src = setupState.payload.otp_image_source;
    otpLink.href = otpAuthUrl(setupState.payload.otp_secret, profileState.payload.user);
    secretElement.textContent = setupState.payload.otp_secret;
    loadingElement.hidden = true;
    qrDetailsElement.hidden = false;
    setupRequiredButtons.forEach((button) => {
      button.disabled = false;
    });
  }

  dialog.querySelector("[data-mfa-start]").addEventListener("click", () => {
    showStep("install");
    if (setupStarted) return;
    setupStarted = true;
    fetchQrCode().catch(showError);
  });

  // Moves forward to the next step, or back to the previous one (Cancel)
  dialog.querySelectorAll("[data-mfa-show-step]").forEach((button) => {
    button.addEventListener("click", () => showStep(button.dataset.mfaShowStep));
  });

  dialog.querySelector("[data-mfa-retry]").addEventListener("click", () => window.location.reload());

  dialog.querySelector("[data-mfa-copy-secret]").addEventListener("click", async () => {
    await navigator.clipboard.writeText(secretElement.textContent);
    secretCopiedElement.hidden = false;
    setTimeout(() => {
      secretCopiedElement.hidden = true;
    }, 2000);
  });

  codeHelpToggle.addEventListener("click", () => {
    codeHelpElement.hidden = !codeHelpElement.hidden;
    codeHelpToggle.setAttribute("aria-expanded", String(!codeHelpElement.hidden));
  });

  codeForm.addEventListener("submit", async (event) => {
    event.preventDefault();
    // Filling the last box submits the form, which could happen again while a code is being checked
    if (submitButton.disabled) return;
    submitButton.disabled = true;
    codeErrorElement.hidden = true;

    try {
      const otpCode = codeInputs.map((codeInput) => codeInput.value).join("");
      const nextState = await currentState.actions.otp_code_verify.run({ otp_code: otpCode });
      if (!enterState(nextState)) return;

      // Hanko returns to the profile's start once the authenticator app is saved. That state is also
      // where the flow starts, so it isn't a tracked step and success is reported from here.
      if (nextState.name === "profile_init") {
        pushFlowEvent("hanko_view_mfa_setup_success", nextState);
        confirmForm.submit();
        return;
      }

      // Otherwise we're still on the setup step, with the reason the code was rejected
      const codeError = nextState.actions.otp_code_verify.inputs?.otp_code?.error;
      // A state that carries its own error has already been reported by the flow tracking
      if (!nextState.error) pushFlowEvent("hanko_flow_error", nextState);
      codeErrorElement.textContent = codeError?.code === "passcode_invalid"
        ? "That code didn't match. Check your authenticator app and try again."
        : (codeError?.message || nextState.error?.message || "Something went wrong. Please try again.");
      codeErrorElement.hidden = false;
      submitButton.disabled = false;
      // Start again with empty boxes, so that entering the next code submits it
      codeInputs.forEach((codeInput) => {
        codeInput.value = "";
      });
      codeInputs[0].focus();
    } catch (error) {
      showError(error);
    }
  });

  if ("mfaStandalone" in dialog.dataset) {
    // Escape would leave an empty page; the modal's skip links go on to the client list instead
    dialog.addEventListener("cancel", (event) => event.preventDefault());
  } else {
    dialog.querySelectorAll("[data-mfa-close]").forEach((button) => button.addEventListener("click", () => dialog.close()));
  }

  dialog.showModal();
}

function mountMfaSetupSafely(dialog) {
  try {
    mountMfaSetup(dialog);
  } catch (error) {
    console.error("Failed to set up authenticator app", error);
  }
}

// The standalone MFA setup page
document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll("[data-hanko-mfa-setup]").forEach(mountMfaSetupSafely);
});

// The modal fetched by the MFA banner. Each fetch replaces the last one, so reopening it starts setup afresh.
document.addEventListener("htmx:load", (event) => {
  if (event.target.matches("[data-hanko-mfa-setup]")) mountMfaSetupSafely(event.target);
});
